import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, LogIn, Lock, GraduationCap, Clock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";

/** Abstract WeCare II figure mark — used only as a subtle branded visual */
function WcMark({ className = "", color = "#1e2a78", size = 220, opacity = 1 }) {
  return (
    <svg
      viewBox="0 0 60 90"
      width={size}
      height={size * 1.5}
      className={className}
      fill="none"
      style={{ opacity }}
      aria-hidden="true"
    >
      <circle cx="30" cy="12" r="8" fill={color} />
      <path
        d="M30 24 C 44 24, 50 36, 46 54 C 44 66, 38 76, 30 84 C 22 76, 16 66, 14 54 C 10 36, 16 24, 30 24 Z"
        fill={color}
      />
    </svg>
  );
}

export default function EmployeePortal() {
  const { user } = useAuth();
  const isEmployee = user && (user.role === "employee" || user.role === "admin");

  return (
    <div data-testid="employee-portal-page" className="bg-brand-cream">
      <PageHeader
        eyebrow="Employee Portal"
        title="A home for the WeCare II"
        accent="team"
        kicker="."
        subtitle="Secure access to your employee tools and resources."
        data-testid="employee-portal-header"
      />

      <section className="wc-container pb-20 md:pb-28" data-testid="employee-portal-body">
        {isEmployee ? (
          /* Signed-in employee/admin — quick links (private) */
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="employee-portal-signed-in">
            <Link
              to="/academy"
              className="group bg-brand-bone rounded-2xl border border-brand-stone/60 p-8 hover:border-brand-navy/30 hover:shadow-[0_18px_40px_rgba(30,42,120,0.08)]"
              style={{ transition: "border-color 200ms ease, box-shadow 200ms ease" }}
              data-testid="employee-portal-academy-link"
            >
              <div className="w-11 h-11 rounded-xl bg-brand-navy/5 grid place-items-center text-brand-navy group-hover:bg-brand-navy group-hover:text-white" style={{ transition: "background-color 200ms ease, color 200ms ease" }}>
                <GraduationCap className="w-5 h-5" strokeWidth={1.7} />
              </div>
              <h3 className="mt-5 font-outfit text-xl font-semibold text-brand-navy">WeCare II Academy</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">Onboarding, training modules, checklists, badges and certificates.</p>
              <div className="mt-5 inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-widest text-brand-navy">
                <span className="border-b border-brand-orange">Open Academy</span> <ArrowRight className="w-3.5 h-3.5 text-brand-orange" />
              </div>
            </Link>

            <Link
              to="/clock"
              className="group bg-brand-bone rounded-2xl border border-brand-stone/60 p-8 hover:border-brand-navy/30 hover:shadow-[0_18px_40px_rgba(30,42,120,0.08)]"
              style={{ transition: "border-color 200ms ease, box-shadow 200ms ease" }}
              data-testid="employee-portal-clock-link"
            >
              <div className="w-11 h-11 rounded-xl bg-brand-navy/5 grid place-items-center text-brand-navy group-hover:bg-brand-navy group-hover:text-white" style={{ transition: "background-color 200ms ease, color 200ms ease" }}>
                <Clock className="w-5 h-5" strokeWidth={1.7} />
              </div>
              <h3 className="mt-5 font-outfit text-xl font-semibold text-brand-navy">Time Clock</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">Clock in and out with GPS, request corrections and view your shifts.</p>
              <div className="mt-5 inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-widest text-brand-navy">
                <span className="border-b border-brand-orange">Open Time Clock</span> <ArrowRight className="w-3.5 h-3.5 text-brand-orange" />
              </div>
            </Link>
          </div>
        ) : (
          /* Public / not signed in — simplified secure gateway (no internal details) */
          <div className="max-w-3xl mx-auto" data-testid="employee-portal-signed-out">
            <div className="relative bg-brand-navy text-white rounded-[1.75rem] p-8 md:p-14 overflow-hidden shadow-[0_20px_50px_rgba(15,26,74,0.20)]">
              {/* subtle branded visual — abstract figure mark, decorative only */}
              <WcMark className="absolute -right-10 -top-10 hidden sm:block" color="#f39325" size={180} opacity={0.10} />
              <WcMark className="absolute -right-4 bottom-[-40px] hidden md:block" color="#ffffff" size={140} opacity={0.06} />

              <div className="relative">
                <div className="inline-flex items-center gap-2.5 text-[10px] md:text-[11px] font-bold uppercase tracking-[0.28em] text-brand-orange">
                  <span className="h-px w-8 bg-brand-orange" />
                  Team Access
                </div>
                <h2 className="mt-5 font-outfit text-3xl md:text-[40px] font-medium tracking-tight leading-[1.1]">
                  Welcome{" "}
                  <span className="italic text-brand-orange" style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}>
                    back
                  </span>.
                </h2>
                <p className="mt-4 text-white/80 text-[15px] md:text-base leading-relaxed max-w-md">
                  Sign in to access your WeCare II Employee Portal.
                </p>
                <Link
                  to="/login"
                  className="mt-8 inline-flex items-center gap-2 rounded-full bg-brand-orange text-white hover:brightness-95 px-8 py-3.5 text-xs font-bold uppercase tracking-[0.22em] shadow-sm"
                  style={{ transition: "filter 200ms ease" }}
                  data-testid="employee-portal-signin-btn"
                >
                  <LogIn className="w-4 h-4" /> Sign In <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </div>

            {/* Understated support message */}
            <div className="mt-8 flex items-start gap-3 max-w-md mx-auto text-center sm:text-left sm:mx-0" data-testid="employee-portal-support-note">
              <Lock className="w-4 h-4 text-brand-navy/50 mt-1 shrink-0 hidden sm:block" strokeWidth={1.6} />
              <div>
                <div className="text-[13px] font-semibold text-brand-navy">Need help accessing your account?</div>
                <p className="mt-1 text-[13px] text-slate-600 leading-relaxed">
                  Contact your supervisor or the WeCare II administrative team.
                </p>
              </div>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
